import React, { forwardRef, useEffect, useRef, useTransition } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSmile, FiSend, FiX } from "react-icons/fi";

const ChatInput = forwardRef(
  (
    {
      newMessage,
      onMessageChange,
      onSendMessage,
      onEmojiToggle,
      replyingTo,
      onCancelReply,
      onTypingStop,
      isE2EEEnabled,
      encryptionReady,
      currentUser,
      otherParticipant,
    },
    ref
  ) => {
    // INDUSTRY STANDARD: Use transitions for non-blocking updates
    const [isPending, startTransition] = useTransition();
    const debounceTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);
    const lastValueRef = useRef(newMessage);
    
    // Sync when parent clears message (after send)
    useEffect(() => {
      if (!isSubmittingRef.current && !newMessage && ref?.current) {
        ref.current.value = "";
        lastValueRef.current = "";
      }
      isSubmittingRef.current = false;
    }, [newMessage, ref]);

    // INSTANT input change - NO state updates, NO re-renders!
    const handleLocalChange = (e) => {
      const value = e.target.value;
      lastValueRef.current = value;
      
      // Clear existing debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Update parent in LOW-PRIORITY transition (won't block typing)
      debounceTimerRef.current = setTimeout(() => {
        startTransition(() => {
          onMessageChange(value);
        });
      }, 50); // Minimal delay, but non-blocking
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Prevent input from blurring (keeps keyboard open)
      if (ref?.current) {
        ref.current.focus();
      }
      
      // Get current value from input directly
      const currentValue = ref?.current?.value || lastValueRef.current;
      
      // Clear debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      isSubmittingRef.current = true;
      
      // Clear input immediately
      if (ref?.current) {
        ref.current.value = "";
        lastValueRef.current = "";
      }
      
      // Update parent and send (in microtask to maintain focus)
      Promise.resolve().then(() => {
        onMessageChange(currentValue);
        onSendMessage(e);
        
        // Ensure focus is maintained
        if (ref?.current) {
          ref.current.focus();
        }
      });
    };

    // Cleanup
    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    return (
      <motion.div
        className={`msgx-input-area ${
          isE2EEEnabled && encryptionReady ? "encrypted" : ""
        }`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        {/* Reply Bar */}
        <AnimatePresence>
          {replyingTo && (
            <motion.div
              className="msgx-reply-bar"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="msgx-reply-bar-content">
                <span className="msgx-reply-bar-header">
                  Replying to{" "}
                  {replyingTo.senderId === "me" ||
                  replyingTo.sender?._id ===
                    (currentUser?.id || currentUser?._id)
                    ? "yourself"
                    : replyingTo.sender?.name || otherParticipant?.name}
                </span>
                <p className="msgx-reply-bar-text">
                  {replyingTo.isDeleted || !replyingTo.content
                    ? "🚫 Message was deleted"
                    : replyingTo.content}
                </p>
              </div>
              <motion.button
                className="msgx-reply-bar-close"
                onClick={onCancelReply}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiX />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Row */}
        <form className="msgx-input-row" onSubmit={handleSubmit}>
          <motion.button
            type="button"
            className="msgx-emoji-btn"
            onClick={onEmojiToggle}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            title="Add emoji"
          >
            <FiSmile />
          </motion.button>

          <input
            ref={ref}
            type="text"
            className="msgx-input-field"
            placeholder="Type a message..."
            defaultValue={newMessage}
            onChange={handleLocalChange}
            onBlur={(e) => {
              // Flush pending update
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                const value = e.target.value;
                startTransition(() => {
                  onMessageChange(value);
                });
              }
              
              // Typing stop only if leaving
              const isLeavingForm = !e.currentTarget.parentElement.contains(e.relatedTarget);
              if (isLeavingForm) {
                onTypingStop();
              }
            }}
          />

          <motion.button
            type="submit"
            className="msgx-send-btn"
            disabled={!newMessage.trim()}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Send message"
          >
            <FiSend />
          </motion.button>
        </form>
      </motion.div>
    );
  }
);

ChatInput.displayName = "ChatInput";

// Memoize to prevent re-renders when parent updates unrelated state
export default React.memo(ChatInput);
